"use client"

import CloseIcon from "@mui/icons-material/Close"
import UpdateIcon from "@mui/icons-material/Update"
import { Box, IconButton, Stack, Typography } from "@mui/material"
import {
  getActiveAnnouncementsQuery,
  GetActiveAnnouncementsQuery,
} from "@tc/ui-dfe/query"
import {
  ANNOUNCEMENT_DISMISSED_KEY,
  sanitizeHtml,
} from "@tc/ui-dfe/utils/helper"
import { useCallback, useState } from "react"
import { useQuery } from "urql"

type Announcement =
  GetActiveAnnouncementsQuery["getActiveAnnouncements"][number]

const isDismissedInSession = (): boolean => {
  try {
    return sessionStorage.getItem(ANNOUNCEMENT_DISMISSED_KEY) === "true"
  } catch {
    return false
  }
}

const setDismissedInSession = (): void => {
  try {
    sessionStorage.setItem(ANNOUNCEMENT_DISMISSED_KEY, "true")
  } catch {
    // sessionStorage unavailable
  }
}

export const AnnouncementBanner = () => {
  const [dismissed, setDismissed] = useState(() => isDismissedInSession())

  const [{ data, fetching }] = useQuery({
    query: getActiveAnnouncementsQuery.toString(),
    requestPolicy: "cache-and-network",
  })

  const handleDismiss = useCallback(() => {
    setDismissedInSession()
    setDismissed(true)
  }, [])

  if (fetching || dismissed) return null

  const announcements = data?.getActiveAnnouncements ?? []

  if (announcements.length === 0) return null

  return (
    <Box
      role="region"
      aria-label="Announcements"
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        padding: "12px 16px",
        gap: "16px",
        width: "100%",
        bgcolor: "info.main",
        borderRadius: 0,
      }}
    >
      <UpdateIcon
        aria-hidden="true"
        sx={{
          width: 16,
          height: 16,
          color: "white",
          mt: "3px",
          flexShrink: 0,
        }}
      />

      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            color: "white",
            letterSpacing: "-0.01em",
            mb: "4px",
          }}
        >
          {announcements.length} Announcement
          {announcements.length > 1 ? "s" : ""}
        </Typography>
        <Stack spacing="4px">
          {announcements.map((announcement: Announcement, index: number) => (
            <Box
              key={announcement.id}
              sx={{
                display: "flex",
                gap: "8px",
                "& a": {
                  color: "inherit",
                  textDecoration: "underline",
                },
                "& p": { margin: 0, display: "inline" },
              }}
            >
              <Typography
                variant="body1"
                component="span"
                sx={{
                  fontWeight: 700,
                  color: "white",
                  letterSpacing: "-0.01em",
                  flexShrink: 0,
                }}
              >
                {index + 1}.
              </Typography>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  fontWeight: 700,
                  color: "white",
                  letterSpacing: "-0.01em",
                }}
              >
                {announcement.title}
              </Typography>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  color: "white",
                  letterSpacing: "-0.01em",
                }}
              >
                {" – "}
              </Typography>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  color: "white",
                  letterSpacing: "-0.01em",
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(announcement.message),
                }}
              />
            </Box>
          ))}
        </Stack>
      </Box>

      <IconButton
        aria-label="Dismiss announcements"
        onClick={handleDismiss}
        sx={{
          padding: "4px",
          color: "white",
          flexShrink: 0,
        }}
      >
        <CloseIcon sx={{ width: 16, height: 16 }} />
      </IconButton>
    </Box>
  )
}
